import { FormattedMessage, useIntl } from 'react-intl'
import { Link, useParams } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircle, faClock } from '@fortawesome/free-solid-svg-icons'
import classNames from 'classnames'
import DOMPurify from 'dompurify'
import { Tooltip, TooltipArrow, TooltipContent, TooltipProvider, TooltipTrigger } from 'ui/Tooltip'
import { temp_createEventDateFromISO } from 'utils/dates'
import { useAppSelector } from '../../../store/hooks'
type HistoryItemType =
   | 'RESERVATION'
   | 'CERTIFICATE'
   | 'MEMBER_CREATE'
   | 'MEMBER_ACTIVATE'
   | 'MEMBER_DEACTIVATE'
   | 'CANCEL_OPERATION'
   | 'TRANSFER_OPERATION'

export type HistoryItem = {
   pointsAccumulated?: number | null
   pointsRedeemed?: number | null
   hotelName: string
   service: string
   id: string
   externalId: number
   createdAt: string
   author?: string
   details: string
   type: HistoryItemType
   customText?: string
   changes?: Array<string>
}

 type HistoryOperationItemType =
  | 'CREATE'
  | 'UPDATE'
  | 'DELETE'
  | 'IMPORT_OPERATION'
  | 'IMPORT_OPERATION_PMS'
  | 'MEMBER_DEACTIVATE'
  | 'MEMBER_ACTIVATE'
  | 'MEMBER_CREATE'
  | 'CANCEL_OPERATION'
  | 'TRANSFER_OPERATION'
  | 'MODIFY_OPERATION'
  | 'MEMBER_UPDATE'
  | 'OPERATION_UPDATE'
  | 'MEMBER_LEVEL_UPDATE'
  | 'MEMBER_SEND_CONFIRMATION_EMAIL'

   export type HistoryOperationItem = Omit<HistoryItem, 'type'> & {
  type: HistoryOperationItemType
}
export type GetBulletFn<T = HistoryItem> = (item: T, className?: string) => React.ReactNode

type Options<T = HistoryItem> = {
   showChanges?: boolean
   getBullet?: GetBulletFn<T>
}

export function defaultGetBullet(item: HistoryItem|HistoryOperationItem , className?: string): React.ReactNode {
   const amber = <FontAwesomeIcon icon={faCircle} size="xs" className={classNames(className, 'text-amber-500')} />
   const green = <FontAwesomeIcon icon={faCircle} size="xs" className={classNames(className, 'text-green-500')} />
   const blue = <FontAwesomeIcon icon={faCircle} size="xs" className={classNames(className, 'text-blue-500')} />
   const red = <FontAwesomeIcon icon={faCircle} size="xs" className={classNames(className, 'text-red-500')} />
   const neutral = <FontAwesomeIcon icon={faCircle} size="xs" className={classNames(className, 'text-neutral-400')} />

   switch (item.type) {
      case 'MEMBER_SEND_CONFIRMATION_EMAIL':
         return neutral
      case 'CREATE':
         return green
      case 'OPERATION_UPDATE':
      case 'MEMBER_UPDATE':
      case 'MODIFY_OPERATION':
      case 'MEMBER_LEVEL_UPDATE':
      case 'TRANSFER_OPERATION':
         return amber
      case 'RESERVATION':
      case 'CERTIFICATE':
         if (item.pointsAccumulated && item.pointsAccumulated > 0) return green
         if (item.pointsRedeemed && item.pointsRedeemed > 0) return blue
         return neutral
      case 'MEMBER_CREATE':
         return green
      case 'MEMBER_ACTIVATE':
         return blue
      case 'MEMBER_DEACTIVATE':
      case 'CANCEL_OPERATION':
         return red
      default:
         return neutral
   }
}

export function useHistoryItemRenderer<T extends HistoryItem | HistoryOperationItem>({ showChanges = false, getBullet = defaultGetBullet }: Options<T> = {}) {
   const intl = useIntl()
   const { accountCode } = useParams()
   const { locale } = useAppSelector((state) => state.base)
   const domPurify = DOMPurify()

   function getRelativeDate(date: string) {
      try {
         const convertedDate = temp_createEventDateFromISO(date).setLocale(locale)
         return (
            <TooltipProvider delayDuration={0}>
               <Tooltip>
                  <TooltipTrigger className="text-neutral-400 ml-5">
                     <FontAwesomeIcon icon={faClock} size="xs" className={classNames('mt-2', 'mr-1')} />
                     {convertedDate.toRelative()}
                  </TooltipTrigger>
                  <TooltipContent className="w-48 text-center">
                     <TooltipArrow />
                     {convertedDate.toLocaleString({
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                     })}
                  </TooltipContent>
               </Tooltip>
            </TooltipProvider>
         )
      } catch (error) {
         console.error('Error parsing date:', error)
         return null
      }
   }

   function renderItem(item: T) {
      const { pointsAccumulated = 0, pointsRedeemed = 0, type } = item

      return (
         <>
            <div className="flex gap-2">
               {getBullet(item, 'mt-1.5')}
               {(pointsAccumulated || pointsRedeemed) && (type === 'RESERVATION' || type === 'CERTIFICATE') ? (
                  <Link to={`/${accountCode}/operations?query=${item.id}`}>
                     <FormattedMessage
                        id="partner.historyItem"
                        values={{
                           name: item.hotelName,
                           service: item.service,
                           points:
                              pointsAccumulated && pointsAccumulated > 0 ? (
                                 <strong>
                                    {intl.formatMessage(
                                       { id: 'partner.points.accumulated' },
                                       { points: item.pointsAccumulated },
                                    )}
                                 </strong>
                              ) : (
                                 <strong>
                                    {intl.formatMessage(
                                       { id: 'partner.points.redeemed' },
                                       { points: item.pointsRedeemed },
                                    )}
                                 </strong>
                              ),
                        }}
                     />
                  </Link>
               ) : (
                  <div>
                     {item.customText ? (
                        <div dangerouslySetInnerHTML={{ __html: domPurify.sanitize(item.customText) }} />
                     ) : (
                        <FormattedMessage
                           id="partner.historyItem.status"
                           values={{
                              author: item.author,
                              status: (
                                 <strong>
                                    {intl.formatMessage(
                                       { id: `partner.historyItem.type.${item.type}` },
                                       { id: item.externalId },
                                    )}
                                 </strong>
                              ),
                           }}
                        />
                     )}
                     {item.details && <p className="text-neutral-500 p-3 border-l-2 block">{item.details}</p>}
                     {showChanges && item.changes && item.changes.length > 0 && (
                        <p className="text-neutral-500 p-3 border-l-2 block">
                           <ul>
                              {item.changes.map((change, idx) => (
                                 <li key={idx}>{change}</li>
                              ))}
                           </ul>
                        </p>
                     )}
                  </div>
               )}
            </div>
            <span className="text-xs text-neutral-400">{getRelativeDate(item.createdAt)}</span>
         </>
      )
   }

   return { renderItem, getBullet, getRelativeDate }
}
